type NamedParticipant = {
  firstname?: string | null;
  lastname?: string | null;
  /** Server-computed full name, used by result queries in place of firstname/lastname. */
  displayName?: string | null;
  _id?: string | null;
};

// Trailing digits of the submission's created timestamp (ms) kept for the anonymous handle. The
// leading digits (year/month) barely change within a study, so the tail identifies a participant.
// 10 digits ≈ a 115-day window, so handles stay unique across any study shorter than that.
const REFERENCE_DIGITS = 10;

/** Derives an anonymous handle (e.g. `#1234567890`) from a submission's created timestamp (ms). */
function formatSubmissionReference(submissionCreated?: number | null): string {
  return submissionCreated ? `#${String(submissionCreated).slice(-REFERENCE_DIGITS)}` : '';
}

/**
 * Participant's full name, falling back to an anonymous handle derived from their submission's
 * created timestamp when no name was collected (e.g. BYO link studies), then to their user id as a
 * last resort. Accepts either a server `displayName` or raw firstname/lastname.
 */
export function getParticipantDisplayName(
  participant?: NamedParticipant | null,
  submissionCreated?: number | null,
): string {
  const name = participant?.displayName || [participant?.firstname, participant?.lastname].filter(Boolean).join(' ');
  return name || formatSubmissionReference(submissionCreated) || participant?._id || '';
}

/**
 * Shortened "Firstname. L" label used for CSV export titles, falling back to the anonymous handle
 * derived from the submission's created timestamp (then user id) when no name was collected
 * (e.g. BYO link studies), matching the on-screen display name.
 */
export function getParticipantAnonymisedName(
  participant?: NamedParticipant | null,
  submissionCreated?: number | null,
): string {
  if (!participant?.firstname && !participant?.lastname) {
    return getParticipantDisplayName(participant, submissionCreated);
  }
  return `${participant.firstname}. ${participant.lastname?.charAt(0)}`;
}
